import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Search, X, Scale, Zap, Plus, Check } from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, Text as RText } from "recharts";
import { usePrefetchOrFetch } from "../common/DataPrefetch";
import CompareInsights from "../common/CompareInsights";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const DIM_LABELS = { care_provisions: "Care", diversity_provisions: "Diversity", governance_provisions: "Governance", geographic_equity: "Geo Equity", extraction_restrictions: "Extract. Restrict.", inclusion_mandates: "Inclusion", agency_protections: "Agency" };
const COLORS = ["#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#3b82f6"];

// Popular countries for quick-select chips (one click vs typing)
const QUICK_PICKS = ["United States", "European Union", "China", "United Kingdom", "Canada", "India", "Japan", "Germany", "Singapore", "Brazil"];

const angleTick = ({ x, y, textAnchor, payload }) => {
  const dx = textAnchor === "start" ? -8 : textAnchor === "end" ? 8 : 0;
  return (
    <RText x={x + dx} y={y + 3} textAnchor="middle" verticalAnchor="middle" fill="#cbd5e1" fontSize={11} fontWeight={600}>
      {payload.value}
    </RText>
  );
};

export const PolicyCompare = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);

  // Use the bootstrap-prefetched policy list — no per-mount network call
  const [policyList] = usePrefetchOrFetch(
    "policyList",
    `${API}/policy/list?limit=400`,
    {
      validate: (d) => d && Array.isArray(d.policies) && d.policies.length > 0,
      delay: 500,
    }
  );

  const allCountries = useMemo(() => {
    if (!policyList?.policies) return [];
    return [...new Set(policyList.policies.filter(p => p.region !== "Corporate").map(p => p.country).filter(Boolean))].sort();
  }, [policyList]);

  // Local filter (instant) — supersedes any network search
  const handleSearch = (q) => {
    setSearchQuery(q);
    if (!q) { setSearchResults([]); return; }
    const lower = q.toLowerCase();
    setSearchResults(
      allCountries
        .filter(c => c.toLowerCase().includes(lower) && !selectedCountries.includes(c))
        .slice(0, 10),
    );
  };

  const addCountry = (c) => {
    if (selectedCountries.length >= 6 || selectedCountries.includes(c)) return;
    setSelectedCountries(prev => [...prev, c]);
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeCountry = (c) => setSelectedCountries(prev => prev.filter(x => x !== c));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchResults.length > 0) {
      e.preventDefault();
      addCountry(searchResults[0]);
    }
  };

  // Comparison fetch with race-guard (rapid country changes don't overwrite latest)
  const reqIdRef = useRef(0);
  useEffect(() => {
    if (selectedCountries.length < 2) { setComparison(null); setMissing([]); return; }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    axios.get(`${API}/policy/compare?countries=${encodeURIComponent(selectedCountries.join(","))}`, { timeout: 30000 })
      .then(r => {
        if (reqIdRef.current === myReq) {
          setComparison(r.data);
          setMissing(r.data.missing || []);
        }
      })
      .catch(() => {
        if (reqIdRef.current === myReq) { setComparison(null); setMissing([]); }
      })
      .finally(() => {
        if (reqIdRef.current === myReq) setLoading(false);
      });
  }, [selectedCountries]);

  const radarData = comparison ? Object.keys(DIM_LABELS).map(dim => {
    const entry = { dimension: DIM_LABELS[dim] };
    comparison.countries.forEach(c => { entry[c.country] = c.dimension_scores?.[dim] || 0; });
    return entry;
  }) : [];

  const availableQuickPicks = QUICK_PICKS.filter(c => !selectedCountries.includes(c) && allCountries.includes(c));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policy-compare">
      <div>
        <h2 className="text-xl font-black text-white">Policy Landscape Compare</h2>
        <p className="text-xs text-slate-400">Pick 2–6 countries to compare AI governance frameworks side-by-side</p>
      </div>

      {/* Search + Selection */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-4">
        {/* Selected pills */}
        {selectedCountries.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="selected-countries">
            {selectedCountries.map((c, i) => (
              <span key={c}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
                style={{ color: COLORS[i % COLORS.length], background: `${COLORS[i % COLORS.length]}15`, border: `1px solid ${COLORS[i % COLORS.length]}30` }}>
                <Check className="w-3 h-3" />
                {c}
                <button onClick={() => removeCountry(c)} data-testid={`remove-${c}`} className="ml-1 hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <span className="text-[10px] text-slate-500 self-center ml-1">
              {selectedCountries.length}/6 selected{selectedCountries.length < 2 && " — pick at least one more"}{selectedCountries.length >= 2 && " · comparison auto-updates"}
            </span>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a country name (e.g. France, Japan, Brazil)..."
            className="w-full pl-8 pr-20 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/30"
            data-testid="compare-search"
            disabled={selectedCountries.length >= 6}
          />
          {searchQuery && searchResults.length > 0 && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none">
              ↵ to add
            </span>
          )}
        </div>

        {searchResults.length > 0 && (
          <div className="border border-white/[0.08] rounded-lg overflow-hidden divide-y divide-white/[0.04]" data-testid="search-results">
            {searchResults.map((c, i) => (
              <button key={c} onClick={() => addCountry(c)}
                className="w-full text-left px-4 py-2 text-xs text-white hover:bg-cyan-500/[0.08] transition-colors flex items-center justify-between group"
                data-testid={`add-${c}`}>
                <span>{c}</span>
                <span className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-[10px] text-cyan-400">
                  <Plus className="w-3 h-3" />Add{i === 0 && " · ↵"}
                </span>
              </button>
            ))}
          </div>
        )}

        {searchQuery && searchResults.length === 0 && (
          <p className="text-xs text-slate-500 px-1" data-testid="search-empty">
            No country matches "{searchQuery}". Try another spelling or pick from below.
          </p>
        )}

        {/* Quick picks */}
        {availableQuickPicks.length > 0 && selectedCountries.length < 6 && (
          <div data-testid="quick-picks">
            <div className="flex items-center gap-1.5 mb-2">
              <Zap className="w-3 h-3 text-amber-400/70" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Quick Pick</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {availableQuickPicks.map(c => (
                <button key={c} onClick={() => addCountry(c)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/[0.04] hover:bg-cyan-500/[0.12] border border-white/[0.08] hover:border-cyan-500/30 text-slate-300 hover:text-cyan-200 transition-all"
                  data-testid={`quick-${c}`}>
                  + {c}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Missing-country warning — surfaced so users see why fewer results appear */}
      {missing.length > 0 && !loading && (
        <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2" data-testid="policy-compare-missing">
          <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex-1 text-[11px] text-amber-200/80">
            <span className="font-semibold">No tracked policies for:</span>{" "}
            <span className="font-mono">{missing.join(", ")}</span>.
            The comparison below reflects only the {comparison?.countries?.length || 0} countries with policy data.
          </div>
        </div>
      )}

      {comparison && comparison.countries.length >= 2 && (
        <>
          {/* AI-generated insight summary (narrative + winners + dim gaps) */}
          <CompareInsights kind="policy" entities={comparison.countries} />

          {/* Radar Chart */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">CII Dimension Comparison</h3>
            <div className="lg:flex lg:items-center lg:gap-6">
              <div className="h-[340px] sm:h-[440px] lg:h-[520px] lg:flex-1" data-testid="policy-radar-chart">
                <ResponsiveContainer>
                  <RadarChart data={radarData} outerRadius="80%" margin={{ top: 14, right: 18, bottom: 14, left: 18 }}>
                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                    <PolarAngleAxis dataKey="dimension" tick={angleTick} />
                    <PolarRadiusAxis tick={{ fill: "#64748b", fontSize: 9 }} domain={[0, 100]} tickCount={5} axisLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#0a0f1f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                      labelStyle={{ color: "#e2e8f0", fontWeight: 700, marginBottom: 4 }}
                      formatter={(v) => `${v}%`}
                    />
                    {comparison.countries.map((c, i) => (
                      <Radar key={c.country} name={c.country} dataKey={c.country}
                        stroke={COLORS[i % COLORS.length]} strokeWidth={2.5}
                        fill={COLORS[i % COLORS.length]} fillOpacity={0.16}
                        dot={{ r: 3, fill: COLORS[i % COLORS.length], strokeWidth: 0 }} />
                    ))}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              {/* Legend: right rail on desktop, row below on mobile */}
              <div className="flex flex-wrap lg:flex-col gap-3 mt-2 lg:mt-0 justify-center lg:justify-start lg:w-44 lg:flex-shrink-0">
                {comparison.countries.map((c, i) => (
                  <span key={c.country} className="flex items-center gap-2 text-[11px] text-slate-300">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="truncate">{c.country}</span>
                    <span className="font-bold ml-auto hidden lg:inline" style={{ color: COLORS[i % COLORS.length] }}>{c.avg_cii}</span>
                  </span>
                ))}
                <p className="hidden lg:block text-[9px] text-slate-500 mt-1 border-t border-white/[0.06] pt-2">Avg CII shown · hover the chart for per-dimension values</p>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                <tr>
                  <th className="text-left p-3 text-slate-400 font-bold">Metric</th>
                  {comparison.countries.map((c, i) => (
                    <th key={c.country} className="text-center p-3 font-bold" style={{ color: COLORS[i % COLORS.length] }}>{c.country}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                <tr>
                  <td className="p-3 text-slate-400 font-medium">Total Policies</td>
                  {comparison.countries.map(c => <td key={c.country} className="p-3 text-center font-bold text-white">{c.policy_count}</td>)}
                </tr>
                <tr>
                  <td className="p-3 text-slate-400 font-medium">Avg CII</td>
                  {comparison.countries.map(c => {
                    const color = c.avg_cii >= 60 ? "#10b981" : c.avg_cii >= 30 ? "#f59e0b" : "#ef4444";
                    return <td key={c.country} className="p-3 text-center font-black" style={{ color }}>{c.avg_cii}</td>;
                  })}
                </tr>
                <tr>
                  <td className="p-3 text-slate-400 font-medium">Has Mandatory Laws</td>
                  {comparison.countries.map(c => <td key={c.country} className="p-3 text-center">{c.has_mandatory ? <span className="text-emerald-400">Yes</span> : <span className="text-red-400">No</span>}</td>)}
                </tr>
                <tr>
                  <td className="p-3 text-slate-400 font-medium">Earliest Policy</td>
                  {comparison.countries.map(c => <td key={c.country} className="p-3 text-center text-white">{c.earliest}</td>)}
                </tr>
                <tr>
                  <td className="p-3 text-slate-400 font-medium">Policy Types</td>
                  {comparison.countries.map(c => <td key={c.country} className="p-3 text-center text-slate-300 text-[9px]">{c.types?.join(", ")}</td>)}
                </tr>
                {Object.entries(DIM_LABELS).map(([dim, label]) => (
                  <tr key={dim}>
                    <td className="p-3 text-slate-400 font-medium">{label}</td>
                    {comparison.countries.map(c => {
                      const val = c.dimension_scores?.[dim] || 0;
                      return <td key={c.country} className="p-3 text-center"><span className={`font-bold ${val >= 50 ? "text-emerald-400" : "text-red-400"}`}>{val}%</span></td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedCountries.length < 2 && !loading && (
        <div className="text-center py-12 space-y-2" data-testid="empty-state">
          <Scale className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-sm text-slate-400">
            {selectedCountries.length === 0
              ? "Pick 2 or more countries to compare their AI policy landscapes"
              : "Select at least one more country to start the comparison."}
          </p>
          {selectedCountries.length === 0 && (
            <p className="text-[11px] text-slate-500">Tap any quick-pick chip above, or search by name. Comparison generates automatically.</p>
          )}
        </div>
      )}

      {loading && <div className="flex justify-center py-12"><div className="w-8 h-8 rounded-full border-2 border-cyan-500/30 border-t-cyan-500 animate-spin" /></div>}
    </div>
  );
};
